import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { setReducedMotion } from '@/components/feature/hairline/kernel';
import { coRoutingStates } from '@/mocks/customerOperations';
import { routingMounts } from './routingFigures';
import type { RoutingHandle } from './routingFigures';
import './routing-system.css';

type Mode = 'auto' | 'human' | 'rest';
type NodeName = 'intake' | 'router' | 'records' | 'review';

const NODES: NodeName[] = ['intake', 'router', 'records', 'review'];
const AUTO_PATH = 'M441 190H477L540 112H618';
const HUMAN_PATH = 'M441 190H477L540 268H611';
const M_AUTO_PATH = 'M219 180H245L278 108H302';
const M_HUMAN_PATH = 'M219 180H245L278 252H300';
const canHover = () => window.matchMedia('(hover: hover)').matches;

/* Interactive request routing system — ported from RoutingSystem_Hairline.html (no iframe). */
export default function RoutingSystem() {
  const [committed, setCommitted] = useState<Mode>('auto');
  const [mode, setMode] = useState<Mode>('auto');
  const [offscreen, setOffscreen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<SVGSVGElement>(null);
  const mobileRef = useRef<SVGSVGElement>(null);
  const nodeRefs = useRef<Record<string, SVGSVGElement | null>>({});
  const handlesRef = useRef<{ name: NodeName; handle: RoutingHandle }[]>([]);

  /* Mount the eight Hairline nodes (desktop + mobile maps); dispose on unmount. */
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reduced = () => setReducedMotion(media.matches);
    reduced();
    media.addEventListener('change', reduced);
    const handles: { name: NodeName; handle: RoutingHandle }[] = [];
    ['d', 'm'].forEach((prefix) => {
      NODES.forEach((name) => {
        const svg = nodeRefs.current[`${prefix}-${name}`];
        if (!svg) return;
        handles.push({ name, handle: routingMounts[name]({ stage: svg, svg, read: { textContent: 'rest' } }, 1) });
      });
    });
    handlesRef.current = handles;
    return () => {
      media.removeEventListener('change', reduced);
      handles.forEach((h) => h.handle.destroy());
      handlesRef.current = [];
    };
  }, []);

  /* Visual + status share one state. */
  useEffect(() => {
    handlesRef.current.forEach(({ name, handle }) => {
      if (name === 'records') handle.select(mode === 'auto');
      else if (name === 'review') handle.select(mode === 'human');
      else handle.select(mode !== 'rest');
    });
  }, [mode]);

  /* Pause SMIL travel dots when offscreen, tab hidden or reduced motion. */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let visible = true;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      const off = !visible || document.hidden || media.matches;
      setOffscreen(off);
      [sceneRef.current, mobileRef.current].forEach((s) => {
        try { if (off) s?.pauseAnimations(); else s?.unpauseAnimations(); } catch { /* noop */ }
      });
    };
    const io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; sync(); }, { threshold: 0.05 });
    io.observe(root);
    document.addEventListener('visibilitychange', sync);
    media.addEventListener('change', sync);
    sync();
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', sync);
      media.removeEventListener('change', sync);
    };
  }, []);

  const preview = useCallback((m: Mode) => setMode(m), []);
  const restore = useCallback(() => setMode(committed), [committed]);
  const commit = (m: Mode) => { setCommitted(m); setMode(m); };

  const onStageOver = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!canHover()) return;
    const hit = (e.target as Element).closest('[data-route]');
    if (hit) preview(hit.getAttribute('data-route') as Mode);
  };
  const onStageOut = (e: ReactPointerEvent<HTMLDivElement>) => {
    const to = e.relatedTarget as Element | null;
    if (!to || !e.currentTarget.contains(to) || !to.closest('[data-route]')) restore();
  };

  const s = coRoutingStates[mode];
  const nodeRef = (key: string) => (el: SVGSVGElement | null) => { nodeRefs.current[key] = el; };

  return (
    <div ref={rootRef} className={`rr-v3 mode-${mode} ${offscreen ? 'rr-offscreen' : ''}`}>
      <div className="rr-feature">
        <div className="rr-editorial">
          <div>
            <div className="rr-kicker rr-mono"><b>Workflow</b> / Customer operations</div>
            <h3>One intake.<br />Two clear routes.</h3>
            <p className="rr-description">AI classifies and routes routine requests. Human review stays available for exceptions and approvals.</p>
          </div>
          <div className="rr-benefits">
            <div className="rr-benefit"><span className="rr-n">01</span><span>Receive requests</span></div>
            <div className="rr-benefit"><span className="rr-n">02</span><span>Classify &amp; route</span></div>
            <div className="rr-benefit"><span className="rr-n">03</span><span>Review when needed</span></div>
          </div>
        </div>

        <div className="rr-system" aria-label="Interactive request routing system" role="group">
          <div className="rr-system-top">
            <span className="rr-mono rr-kicker">System / Request routing</span>
            <span className="rr-live rr-mono"><i />{s.header}</span>
          </div>

          <div className="rr-figure-stage" onPointerOver={onStageOver} onPointerOut={onStageOut}>
            <svg ref={sceneRef} className="rr-figure-svg" viewBox="0 0 800 335" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Requests enter the intake tray and classification module, then route to updated records or human review.">
              <path d="M198 190H326" className="rr-wire-mid" />
              <path d={AUTO_PATH} className="rr-wire-mid" />
              <path d={HUMAN_PATH} className="rr-wire-mid" />
              <path d={AUTO_PATH} className="rr-route-active rr-route-auto" />
              <path data-route="auto" d={AUTO_PATH} className="rr-hit-path" />
              <circle className="rr-travel rr-travel-auto" r="3"><animateMotion dur="4.2s" repeatCount="indefinite" path={AUTO_PATH} /></circle>
              <path d={HUMAN_PATH} className="rr-route-active rr-route-human" />
              <path data-route="human" d={HUMAN_PATH} className="rr-hit-path" />
              <circle className="rr-travel rr-travel-human" r="3"><animateMotion dur="4.2s" repeatCount="indefinite" path={HUMAN_PATH} /></circle>
              <svg ref={nodeRef('d-intake')} className="rr-node-svg rr-node-intake" data-controlled="true" x="45" y="102" width="200" height="160" viewBox="0 0 400 320" aria-hidden="true" />
              <svg ref={nodeRef('d-router')} className="rr-node-svg rr-node-router" data-controlled="true" x="282" y="94" width="210" height="168" viewBox="0 0 400 320" aria-hidden="true" />
              <svg ref={nodeRef('d-records')} className="rr-node-svg rr-node-records" data-controlled="true" data-route="auto" x="557" y="19" width="205" height="164" viewBox="0 0 400 320" aria-hidden="true" />
              <svg ref={nodeRef('d-review')} className="rr-node-svg rr-node-review" data-controlled="true" data-route="human" x="557" y="170" width="205" height="164" viewBox="0 0 400 320" aria-hidden="true" />
              <text className="rr-routing-label" x="86" y="107">01 / INTAKE</text>
              <text className="rr-routing-label" x="340" y="101">02 / CLASSIFY</text>
              <text className="rr-routing-label" x="610" y="28">03 / ROUTINE</text>
              <text className="rr-routing-label" x="611" y="181">04 / EXCEPTION</text>
            </svg>

            <svg ref={mobileRef} className="rr-mobile-map" viewBox="0 0 400 320" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Requests enter the intake tray and classification module, then route to updated records or human review.">
              <path d="M92 180H150" className="rr-wire-mid" />
              <path d={M_AUTO_PATH} className="rr-wire-mid" />
              <path d={M_HUMAN_PATH} className="rr-wire-mid" />
              <path d={M_AUTO_PATH} className="rr-route-active rr-route-auto" />
              <path data-route="auto" d={M_AUTO_PATH} className="rr-hit-path" />
              <circle className="rr-travel rr-travel-auto" r="3"><animateMotion dur="4.2s" repeatCount="indefinite" path={M_AUTO_PATH} /></circle>
              <path d={M_HUMAN_PATH} className="rr-route-active rr-route-human" />
              <path data-route="human" d={M_HUMAN_PATH} className="rr-hit-path" />
              <circle className="rr-travel rr-travel-human" r="3"><animateMotion dur="4.2s" repeatCount="indefinite" path={M_HUMAN_PATH} /></circle>
              <svg ref={nodeRef('m-intake')} className="rr-node-svg rr-node-intake" data-controlled="true" x="0" y="125" width="130" height="104" viewBox="0 0 400 320" aria-hidden="true" />
              <svg ref={nodeRef('m-router')} className="rr-node-svg rr-node-router" data-controlled="true" x="119" y="120" width="135" height="108" viewBox="0 0 400 320" aria-hidden="true" />
              <svg ref={nodeRef('m-records')} className="rr-node-svg rr-node-records" data-controlled="true" data-route="auto" x="258" y="31" width="130" height="104" viewBox="0 0 400 320" aria-hidden="true" />
              <svg ref={nodeRef('m-review')} className="rr-node-svg rr-node-review" data-controlled="true" data-route="human" x="258" y="198" width="130" height="104" viewBox="0 0 400 320" aria-hidden="true" />
              <text className="rr-routing-label" x="22" y="116">INTAKE</text>
              <text className="rr-routing-label" x="155" y="113">CLASSIFY</text>
              <text className="rr-routing-label" x="300" y="20">AUTO</text>
              <text className="rr-routing-label" x="295" y="190">REVIEW</text>
            </svg>
          </div>

          <div className="rr-readout" aria-live="polite">
            <div className="rr-readout-top rr-mono">
              <span>Trace / Representative request</span>
              <span className="rr-tag">{s.tag}</span>
            </div>
            <div className="rr-events">
              <div className="rr-event"><small>001 / INTAKE</small><span>Request received</span></div>
              <div className="rr-event"><small>002 / CLASSIFY</small><span>{s.classify}</span></div>
              <div className="rr-event"><small>003 / ROUTE</small><span className="rr-event-state">{s.action}</span></div>
            </div>
          </div>

          <div className="rr-controls">
            <div className="rr-controls-caption rr-mono">Demo / 02 routes</div>
            <div className="rr-button-group" role="group" aria-label="Select request route">
              {([['auto', 'Automated'], ['human', 'Human review'], ['rest', 'Reset']] as [Mode, string][]).map(([m, label]) => (
                <button
                  key={m}
                  type="button"
                  className="rr-mode cursor-target uppercase"
                  aria-pressed={committed === m}
                  onClick={() => commit(m)}
                  onMouseEnter={() => { if (canHover()) preview(m); }}
                  onMouseLeave={restore}
                  onFocus={() => preview(m)}
                  onBlur={restore}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}